import type { NisStatementEvent } from '@avisoma/shared';
import { Badge } from '../../../catalyst/badge';
import { SourcePageCell } from './SourcePageCell';
import { Td, Th, TruncatedTd } from './cells';
import { FindingMarkerCell, FindingMarkerHeader, type RowFindings } from './FindingMarkerCell';
import {
  extractionRowClass,
  extractionTableClass,
  extractionTbodyClass,
  extractionTheadClass,
} from './tableClasses';

// Consolidated rows can carry `null` for a contradictory declaration (not just absent).
type NisRow = Omit<NisStatementEvent, 'noIncidentDeclaration'> & {
  pageId?: string;
  noIncidentDeclaration?: boolean | null;
};

/**
 * Dense grid for `nisStatementEvents` (No Incident/Accident Statement rows, DR-3261).
 * Used on the entity view (with a source-page column) and the page view (pass no
 * `pageIdToPosition`). Booleans stay tri-state: Yes/No only when defined.
 */
export function NisTable({
  rows,
  pageIdToPosition,
  onInspect,
  rowFindings,
}: {
  rows: NisRow[];
  pageIdToPosition?: Map<string, number>;
  onInspect: (row: NisRow, index: number) => void;
  rowFindings?: RowFindings;
}) {
  return (
    <table className={extractionTableClass}>
      <thead className={extractionTheadClass}>
        <tr>
          {rowFindings && <FindingMarkerHeader />}
          {pageIdToPosition && <Th>Page</Th>}
          <Th>Date</Th>
          <Th>Coverage Start</Th>
          <Th>Coverage End</Th>
          <Th>Start TSN</Th>
          <Th>Start CSN</Th>
          <Th>End TSN</Th>
          <Th>End CSN</Th>
          <Th>Operator</Th>
          <Th>Issuer</Th>
          <Th>Issuer Role</Th>
          <Th>ESN</Th>
          <Th>Subject</Th>
          <Th>Aircraft</Th>
          <Th>No Incident</Th>
          <Th>Signed</Th>
        </tr>
      </thead>
      <tbody className={extractionTbodyClass}>
        {rows.map((r, i) => (
          <tr
            key={`${r.pageId ?? 'page'}-${i}`}
            onClick={() => onInspect(r, i)}
            className={extractionRowClass}
          >
            {rowFindings && <FindingMarkerCell findings={rowFindings.get(i)} />}
            {pageIdToPosition && (
              <Td>
                <SourcePageCell pageId={r.pageId} pageIdToPosition={pageIdToPosition} />
              </Td>
            )}
            <Td>{r.statementDate ?? '—'}</Td>
            <Td>{formatCoverageStart(r)}</Td>
            <Td>{r.coveragePeriodEnd ?? '—'}</Td>
            <Td>{r.coverageStartTsn ?? '—'}</Td>
            <Td>{r.coverageStartCsn ?? '—'}</Td>
            <Td>{r.coverageEndTsn ?? '—'}</Td>
            <Td>{r.coverageEndCsn ?? '—'}</Td>
            <TruncatedTd value={r.operatorName} />
            <TruncatedTd value={r.issuer} />
            <Td>{formatIssuerRole(r.issuerRole)}</Td>
            {/* The monospace treatment belongs to a printed serial, not to the badge. */}
            <Td className={r.esn ? 'font-mono' : undefined}>
              <EsnCell row={r} />
            </Td>
            <Td>{formatSubject(r.statementSubject)}</Td>
            <TruncatedTd value={formatAircraft(r)} />
            <Td>{formatDefinedBoolean(r.noIncidentDeclaration)}</Td>
            <Td>{formatDefinedBoolean(r.isSigned)}</Td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/**
 * ESN cell. A statement whose engines live in an ATTACHED schedule prints no serial by design
 * (DR-4329) — the badge tells that apart from a row that simply has no engine, so an empty cell
 * never reads as a failed extraction.
 */
function EsnCell({ row }: { row: NisRow }) {
  if (row.esn && row.coversAttachedEngineList) {
    return (
      <span className="flex items-center gap-1">
        {row.esn}
        <Badge color="zinc">Attached list</Badge>
      </span>
    );
  }
  if (row.esn) return <>{row.esn}</>;
  if (row.coversAttachedEngineList) return <Badge color="zinc">Attached list</Badge>;
  return <>—</>;
}

/** A period that starts at the engine's birth prints no start date (DR-5210). */
function formatCoverageStart(row: NisRow): string {
  if (row.coveragePeriodStart) return row.coveragePeriodStart;
  return row.coverageSinceNew === true ? 'Since new' : '—';
}

function formatIssuerRole(role: NisStatementEvent['issuerRole']): string {
  if (role === 'operator') return 'Operator';
  if (role === 'camo_on_behalf') return 'CAMO on behalf';
  if (role === 'custodian') return 'Custodian';
  return '—';
}

/**
 * What the declaration is about (DR-4622). An `Aircraft` row's coverage period is the aircraft's,
 * not the engine's own — the row is one of the engines installed on it.
 */
function formatSubject(subject: NisStatementEvent['statementSubject']): string {
  if (subject === 'aircraft') return 'Aircraft';
  if (subject === 'engine') return 'Engine';
  return '—';
}

/** Registration and MSN of the aircraft an aircraft-level statement names, e.g. `EC-IEG · MSN 1674`. */
function formatAircraft(row: NisRow): string | undefined {
  const parts = [row.aircraftRegistration, row.aircraftMsn ? `MSN ${row.aircraftMsn}` : undefined].filter(Boolean);
  return parts.length > 0 ? parts.join(' · ') : undefined;
}

function formatDefinedBoolean(value: boolean | null | undefined): 'Yes' | 'No' | 'Conflict' | '—' {
  if (value === true) return 'Yes';
  if (value === false) return 'No';
  // Consolidated `null` means source pages disagreed (not merely absent).
  if (value === null) return 'Conflict';
  return '—';
}
